import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { UserRole } from '@jalupro/shared';
import { AuthProvider, RequireAuth, RequireRole, ADMIN_AREA_ROLES, LanguageSync, useAuth, homePathForRole } from './auth';
import { MODULES } from './modules';
import { OfficesProvider } from './offices';
import { AgentLayout, AdminLayout } from './layouts';

// Public / standalone
import LoginPage from './screens/Login';
import PrivacyPage from './screens/Privacy';
import PipelinePage from './screens/Pipeline';
import RootRedirect from './screens/RootRedirect';
import NotFound from './screens/NotFound';

// Agent portal screens (placeholders; one screen agent each)
import AgentDashboard from './screens/agent/Dashboard';
import OrderForm from './screens/agent/OrderForm';
import AgentOrders from './screens/agent/Orders';
import AgentClients from './screens/agent/Clients';
import ClientForm from './screens/agent/ClientForm';
import AgentSettings from './screens/agent/Settings';

// Admin portal screens
import AdminDashboard from './screens/admin/Dashboard';
import AdminOrdersQueue from './screens/admin/OrdersQueue';
import AdminPayments from './screens/admin/Payments';
import AdminOrderDetail from './screens/admin/OrderDetail';
import AdminAgents from './screens/admin/Agents';
import AdminClients from './screens/admin/Clients';
import ReassignClients from './screens/admin/ReassignClients';
import AdminReports from './screens/admin/Reports';
import AdminUsers from './screens/admin/Users';
import AdminSettings from './screens/admin/Settings';
import AdminProfile from './screens/admin/Profile';
import Products from './screens/Products';

/**
 * Application router (single host, PATH routing — DECISIONS.md):
 *   /login, /privacy, /pipeline (standalone),
 *   /agent/*  → AgentLayout (AGENT only),
 *   /admin/*  → AdminLayout (ADMIN/ACCOUNTANT/SUPER_ADMIN; role-gated inside),
 *   /         → role-based redirect.
 *
 * This route graph is FROZEN by the Web-foundation step. Screen agents fill the
 * placeholder screen files; they do not change routing.
 */
export default function App() {
  return (
    <AuthProvider>
      <LanguageSync />
      <OfficesProvider>
      <BrowserRouter>
        <Routes>
          {/* Public + standalone */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/pipeline" element={<PipelinePage />} />

          {/* Agent portal — AGENT only */}
          <Route element={<RequireAuth roles={[UserRole.AGENT]} />}>
            <Route path="/agent" element={<AgentLayout />}>
              <Route
                index
                element={
                  <Navigate
                    to={MODULES.agentPortal ? '/agent/dashboard' : MODULES.clients ? '/agent/clients' : '/agent/settings'}
                    replace
                  />
                }
              />
              {MODULES.agentPortal && (
                <>
                  <Route path="dashboard" element={<AgentDashboard />} />
                  <Route path="orders/new" element={<OrderForm />} />
                  <Route path="orders" element={<AgentOrders />} />
                  <Route path="products" element={<Products />} />
                </>
              )}
              {MODULES.clients && (
                <>
                  <Route path="clients" element={<AgentClients />} />
                  <Route path="clients/new" element={<NewClientPage />} />
                  <Route path="clients/:id/edit" element={<EditClientPage />} />
                </>
              )}
              <Route path="settings" element={<AgentSettings />} />
            </Route>
          </Route>

          {/* Admin portal — ADMIN / ACCOUNTANT / SUPER_ADMIN (role-gated inside) */}
          <Route element={<RequireAuth roles={ADMIN_AREA_ROLES} />}>
            <Route path="/admin" element={<AdminLayout />}>
              {/* Modules not yet approved are hidden (modules.ts); the index lands on
                  the first visible item for the role. */}
              <Route index element={MODULES.dashboard ? <AdminDashboard /> : <AdminHome />} />
              {MODULES.orders && (
                <>
                  <Route path="orders" element={<AdminOrdersQueue />} />
                  <Route path="orders/:id" element={<AdminOrderDetail />} />
                </>
              )}
              {MODULES.payments && <Route path="payments" element={<AdminPayments />} />}
              <Route
                path="agents"
                element={
                  <RequireRole cap="agents.list">
                    <AdminAgents />
                  </RequireRole>
                }
              />
              {MODULES.clients && (
                <>
                  <Route
                    path="clients"
                    element={
                      <RequireRole cap="clients.view">
                        <AdminClients />
                      </RequireRole>
                    }
                  />
                  <Route
                    path="clients/reassign"
                    element={
                      <RequireRole cap="clients.reassign">
                        <ReassignClients />
                      </RequireRole>
                    }
                  />
                  <Route
                    path="clients/new"
                    element={
                      <RequireRole cap="clients.write">
                        <AdminNewClientPage />
                      </RequireRole>
                    }
                  />
                  <Route
                    path="clients/:id/edit"
                    element={
                      <RequireRole cap="clients.write">
                        <AdminEditClientPage />
                      </RequireRole>
                    }
                  />
                </>
              )}
              {MODULES.products && <Route path="products" element={<Products />} />}
              {MODULES.reports && (
                <Route
                  path="reports"
                  element={
                    <RequireRole cap="reports.moneyOps">
                      <AdminReports />
                    </RequireRole>
                  }
                />
              )}
              <Route
                path="users"
                element={
                  <RequireRole cap="users.manage">
                    <AdminUsers />
                  </RequireRole>
                }
              />
              <Route
                path="settings"
                element={
                  <RequireRole cap="settings.manage">
                    <AdminSettings />
                  </RequireRole>
                }
              />
              {/* Own account — every admin-portal role. */}
              <Route path="profile" element={<AdminProfile />} />
            </Route>
          </Route>

          {/* Root redirect + catch-all */}
          <Route path="/" element={<RootRedirect />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
      </OfficesProvider>
    </AuthProvider>
  );
}

/** /admin index while the dashboard module is off: go to the role's first visible page. */
function AdminHome() {
  const { role } = useAuth();
  return <Navigate to={role ? homePathForRole(role) : '/login'} replace />;
}

/** Route wrapper: create a new client, then return to the directory — or, when opened
 *  from the order form (`?next=order`), back to a new order with the client preselected. */
function NewClientPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const toOrder = params.get('next') === 'order';
  return (
    <ClientForm
      onDone={(saved) =>
        navigate(toOrder ? `/agent/orders/new?clientId=${saved.id}` : '/agent/clients')
      }
      onCancel={() => navigate(toOrder ? '/agent/orders/new' : '/agent/clients')}
    />
  );
}

/** Route wrapper: edit an existing client (`:id`), then return to the directory — or,
 *  when opened mid-order (`?next=order`), back to the order with the client preselected. */
function EditClientPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  const toOrder = params.get('next') === 'order';
  return (
    <ClientForm
      clientId={id}
      onDone={(saved) =>
        navigate(toOrder ? `/agent/orders/new?clientId=${saved.id}` : '/agent/clients')
      }
      onCancel={() => navigate(toOrder ? `/agent/orders/new?clientId=${id}` : '/agent/clients')}
    />
  );
}

/** Admin route wrapper: create a client, then return to the admin directory. */
function AdminNewClientPage() {
  const navigate = useNavigate();
  return (
    <ClientForm
      onDone={() => navigate('/admin/clients')}
      onCancel={() => navigate('/admin/clients')}
    />
  );
}

/** Admin route wrapper: edit an existing client (`:id`), then return to the directory. */
function AdminEditClientPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  return (
    <ClientForm
      clientId={id}
      onDone={() => navigate('/admin/clients')}
      onCancel={() => navigate('/admin/clients')}
    />
  );
}
